const ADMIN_ROLE = "ROLE_ADMIN";
let cachedUser = null;
let cachedUserRaw = null;
let cacheInvalidated = true;

export const RBAC_PRIVILEGES = {
  viewUsers: ["READ_USERS_DATA", "EDIT_USERS_DATA"],
  manageUsers: ["EDIT_USERS_DATA"],
  viewRoles: ["READ_PRIVILEGE", "WRITE_PRIVILEGE"],
  manageRoles: ["WRITE_PRIVILEGE"],
  viewPrivileges: ["READ_PRIVILEGE", "WRITE_PRIVILEGE"],
  managePrivileges: ["WRITE_PRIVILEGE"],
};

function getStoredUser() {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem("user");
  console.log("RBAC ACCESS USER: " + stored)
  if (!cacheInvalidated && cachedUser && stored === cachedUserRaw) {
    return cachedUser;
  }
  if (!stored) {
    resetRbacAccessCache();
    return null;
  }
  try {
    const parsed = JSON.parse(stored);
    cachedUserRaw = stored;
    cachedUser = parsed.user || parsed;
    cacheInvalidated = false;
    return cachedUser;
  } catch (error) {
    resetRbacAccessCache();
    return null;
  }
}

function normalizeValue(value, fallbackKeys = []) {
  if (value == null) return null;
  if (typeof value === "string") return value;
  for (const key of fallbackKeys) {
    if (Object.prototype.hasOwnProperty.call(value, key) && value[key] != null) {
      return value[key];
    }
  }
  return null;
}

/**
 * Reset cached RBAC user data (use after login/logout or role/privilege updates).
 */
export function resetRbacAccessCache() {
  cachedUser = null;
  cachedUserRaw = null;
  cacheInvalidated = true;
}

export function getCurrentUserRoles() {
  const user = getStoredUser();
  if (!user) return [];
  const rawRoles =
    user.roles ||
    user.roleDtos ||
    user.authorities ||
    user.userRoles ||
    user.roleKeys ||
    user.roleNames ||
    [];

  return (Array.isArray(rawRoles) ? rawRoles : [rawRoles])
    .map((role) =>
      normalizeValue(role, ["name", "key", "roleKey", "authority", "code"])
    )
    .filter(Boolean);
}

export function getCurrentUserPrivileges() {
  const user = getStoredUser();
  if (!user) return [];
  const rawPrivileges =
    user.privileges ||
    user.privilegeDtos ||
    user.privilegeKeys ||
    user.permissions ||
    user.authorities ||
    [];

  return (Array.isArray(rawPrivileges) ? rawPrivileges : [rawPrivileges])
    .map((privilege) =>
      normalizeValue(privilege, ["name", "key", "privilegeKey", "authority", "code"])
    )
    .filter(Boolean);
}

export function normalizeRoleName(role) {
  return normalizeValue(role, ["name", "key", "roleKey", "authority", "code"]);
}

export function normalizeRolesPayload(payload) {
  const rolesList = Array.isArray(payload) ? payload : payload?.content || [];
  return rolesList.map((role) => normalizeRoleName(role)).filter(Boolean);
}

export function userHasRole(role) {
  return getCurrentUserRoles().includes(role);
}

export function userHasAnyRole(roles) {
  return roles.some((role) => getCurrentUserRoles().includes(role));
}

export function userHasAnyPrivilege(privileges) {
  const currentPrivileges = getCurrentUserPrivileges();
  return privileges.some((privilege) => currentPrivileges.includes(privilege));
}

export function canAccessRbacManager() {
  return (
    userHasRole(ADMIN_ROLE) ||
    userHasAnyPrivilege([
      ...RBAC_PRIVILEGES.viewUsers,
      ...RBAC_PRIVILEGES.viewRoles,
      ...RBAC_PRIVILEGES.viewPrivileges,
    ])
  );
}

export function canViewUsers() {
  return userHasRole(ADMIN_ROLE) || userHasAnyPrivilege(RBAC_PRIVILEGES.viewUsers);
}

export function canManageUsers() {
  return userHasRole(ADMIN_ROLE) || userHasAnyPrivilege(RBAC_PRIVILEGES.manageUsers);
}

export function canViewRoles() {
  return userHasRole(ADMIN_ROLE) || userHasAnyPrivilege(RBAC_PRIVILEGES.viewRoles);
}

export function canManageRoles() {
  return userHasRole(ADMIN_ROLE) || userHasAnyPrivilege(RBAC_PRIVILEGES.manageRoles);
}

export function canViewPrivileges() {
  return userHasRole(ADMIN_ROLE) || userHasAnyPrivilege(RBAC_PRIVILEGES.viewPrivileges);
}

export function canManagePrivileges() {
  return userHasRole(ADMIN_ROLE) || userHasAnyPrivilege(RBAC_PRIVILEGES.managePrivileges);
}
